/**
 * Used to specify an instance type when e.g. used as key on a class.
 * This allows to register and obtain instances by their type easily and safely (no clashing).
 * @type {symbol}
 */
import {doNothing} from "./functional-utils.js";

export const $type = Symbol('$type');

/**
 * Used to specify as key for a special function e.g. on services that will be called when linking.
 * This is very useful to easily inject services into other services.
 * @type {symbol}
 */
export const $inject = Symbol('$inject');

/**
 * Very simple DI that can be extended for more features
 * @type {{register(*, *): void, obtain(*): *}}
 */
export class DiContainer {

  /**
   * @private
   * @type {Object.<string|Symbol, any>}
   */
  _registry = {};

  /**
   * @private
   * @type {any[]}
   */
  _registryList = []

  /**
   * Calls instance inject method (if any) and provides access to obtain (for actual injection) and applyInject (for further propagation use)
   * @param {any} instance
   */
  applyInject = instance => (instance[$inject] || doNothing).bind(instance)(this.obtain.bind(this), this.applyInject);

  /**
   * @param {string|Symbol} key
   * @param {any} instance
   */
  register(key, instance) {
    this._registry[key[$type] || key] = instance;
    this._registryList.push(instance);
  }

  /**
   * @param {string|Symbol} key
   * @returns {any}
   */
  obtain(key) {
    return this._registry[key[$type] || key];
  }

  /**
   * Call this method so each registered instance will apply it's {@link $inject} keyed method (if any is defined).
   * This effectively links all services together at a point where all instances are available.
   */
  link() {
    // it basically calls $inject keyed method on each object in registry that defines this
    this._registryList.forEach(instance => this.applyInject(instance));
  }

}
